/*
 * The console's stylesheet.
 *
 * Built around one constraint: an operator looks at this screen for eight
 * hours. So the contrast is calm, the density is high, nothing moves unless the
 * operator did something, and the one loud thing on the page — the waiting
 * strip on a conversation — carries information: how long someone has been
 * left without an answer.
 *
 * Every colour is a token. The dark theme redefines the tokens; no rule below
 * mentions a theme.
 */

:root {
  --canvas: #eef1f4;
  --surface: #ffffff;
  --raised: #f5f7f9;
  --line: #dce1e7;
  --line-strong: #c9d0d9;
  --text: #18202b;
  --muted: #667180;
  --faint: #8a94a2;

  --sea: #0e7c7b;
  --sea-ink: #ffffff;
  --sea-soft: #e0f1f0;
  --sea-text: #0b6464;

  --wait-fresh: #0e7c7b;
  --wait-warm: #d08a06;
  --wait-hot: #c2410c;

  --note: #fbf1cf;
  --note-line: #ecd78a;
  --danger: #c0262d;
  --danger-soft: #fbe5e6;
  --ok: #15803d;

  /* The rail is ink in both themes: the console's one constant. */
  --rail: #18202b;
  --rail-text: #aab4c2;
  --rail-hover: #243040;

  --radius-s: 6px;
  --radius: 10px;
  --radius-l: 14px;
  --shadow-pop: 0 10px 30px rgba(24, 32, 43, 0.16), 0 2px 6px rgba(24, 32, 43, 0.08);

  --font: "Segoe UI Variable Text", "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
  color-scheme: light;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --canvas: #11151b;
    --surface: #181e26;
    --raised: #202833;
    --line: #2a3340;
    --line-strong: #364252;
    --text: #e6eaf0;
    --muted: #93a0b0;
    --faint: #6f7b8b;
    --sea: #2bb3a8;
    --sea-ink: #07201f;
    --sea-soft: #173a3a;
    --sea-text: #6fd6cc;
    --wait-fresh: #2bb3a8;
    --wait-warm: #e3a008;
    --wait-hot: #f0643a;
    --note: #3a3320;
    --note-line: #5c4f26;
    --danger: #f06d6d;
    --danger-soft: #3b2023;
    --ok: #4ade80;
    --rail: #0b0f14;
    --rail-hover: #1a222d;
    --shadow-pop: 0 10px 30px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3);
    color-scheme: dark;
  }
}

:root[data-theme="dark"] {
  --canvas: #11151b;
  --surface: #181e26;
  --raised: #202833;
  --line: #2a3340;
  --line-strong: #364252;
  --text: #e6eaf0;
  --muted: #93a0b0;
  --faint: #6f7b8b;
  --sea: #2bb3a8;
  --sea-ink: #07201f;
  --sea-soft: #173a3a;
  --sea-text: #6fd6cc;
  --wait-fresh: #2bb3a8;
  --wait-warm: #e3a008;
  --wait-hot: #f0643a;
  --note: #3a3320;
  --note-line: #5c4f26;
  --danger: #f06d6d;
  --danger-soft: #3b2023;
  --ok: #4ade80;
  --rail: #0b0f14;
  --rail-hover: #1a222d;
  --shadow-pop: 0 10px 30px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3);
  color-scheme: dark;
}

* { box-sizing: border-box; }

html, body { height: 100%; }
body {
  margin: 0;
  background: var(--canvas);
  color: var(--text);
  font: 14px/1.5 var(--font);
  -webkit-font-smoothing: antialiased;
}

button, input, select, textarea { font: inherit; color: inherit; }
button { cursor: pointer; }
a { color: var(--sea-text); }
:focus-visible { outline: 2px solid var(--sea); outline-offset: 2px; }
.num { font-variant-numeric: tabular-nums; }
.hint { color: var(--muted); font-size: 12px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* ------------------------------------------------------------------ buttons */

.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: 1px solid var(--line-strong); background: var(--surface); color: var(--text);
  border-radius: var(--radius); padding: 7px 14px; font-size: 14px; line-height: 1.3;
  text-decoration: none; white-space: nowrap;
}
.btn:hover { background: var(--raised); }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn.primary { background: var(--sea); color: var(--sea-ink); border-color: transparent; font-weight: 600; }
.btn.primary:hover { filter: brightness(1.06); }
.btn.danger { color: var(--danger); }
.btn.ghost { background: transparent; border-color: transparent; }
.btn.ghost:hover { background: var(--raised); }
.btn.sm { padding: 4px 10px; font-size: 13px; }
.btn.icon { padding: 6px; width: 32px; height: 32px; }

/* -------------------------------------------------------------------- shell */

.shell { display: grid; grid-template-columns: 64px minmax(0, 1fr); height: 100vh; }

.rail {
  background: var(--rail); color: var(--rail-text);
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 12px 0;
}
.rail .brand {
  width: 36px; height: 36px; border-radius: 10px; margin-bottom: 14px;
  background: var(--sea); color: #fff; display: grid; place-items: center; font-weight: 700; font-size: 17px;
}
.rail button {
  position: relative; width: 44px; height: 44px; border-radius: 10px; border: none;
  background: transparent; color: inherit; display: grid; place-items: center;
}
.rail button:hover { background: var(--rail-hover); color: #fff; }
.rail button[aria-current="page"] { color: #fff; background: var(--rail-hover); }
.rail button[aria-current="page"]::before {
  content: ""; position: absolute; left: -10px; top: 10px; bottom: 10px; width: 3px;
  border-radius: 0 3px 3px 0; background: var(--sea);
}
.rail .count {
  position: absolute; top: 4px; right: 3px; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 9px; background: var(--wait-hot); color: #fff; font-size: 11px; font-weight: 700;
  display: grid; place-items: center;
}
.rail .spacer { flex: 1; }
.rail .presence { width: 10px; height: 10px; border-radius: 50%; border: 2px solid var(--rail); position: absolute; right: 6px; bottom: 6px; }
.presence.online { background: var(--ok); }
.presence.away { background: var(--wait-warm); }
.presence.offline { background: var(--faint); }

.view { min-width: 0; height: 100vh; overflow: hidden; }

/* -------------------------------------------------------------------- inbox */

.inbox {
  display: grid; grid-template-columns: 320px minmax(0, 1fr) 320px; height: 100%;
}
.pane { display: flex; flex-direction: column; min-width: 0; min-height: 0; background: var(--surface); }
.pane + .pane { border-left: 1px solid var(--line); }

.list-head { padding: 14px 14px 10px; border-bottom: 1px solid var(--line); }
.list-head h1 { font-size: 16px; font-weight: 650; margin: 0 0 10px; }
.tabs { display: flex; flex-wrap: wrap; gap: 4px; }
.tabs button {
  border: none; background: transparent; color: var(--muted); border-radius: var(--radius-s);
  padding: 5px 8px; font-size: 13px; display: inline-flex; gap: 6px; align-items: center;
}
.tabs button:hover { background: var(--raised); color: var(--text); }
.tabs button[aria-selected="true"] { background: var(--sea-soft); color: var(--sea-text); font-weight: 600; }
.tabs .n { font-size: 12px; font-variant-numeric: tabular-nums; opacity: 0.85; }
.search { margin-top: 10px; position: relative; }
.search input {
  width: 100%; padding: 7px 10px 7px 32px; border-radius: var(--radius);
  border: 1px solid var(--line); background: var(--raised);
}
.search svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--faint); }

.rows { overflow-y: auto; flex: 1; }
.rows .empty { padding: 40px 20px; }
.row {
  position: relative; display: grid; grid-template-columns: 36px minmax(0, 1fr); gap: 10px;
  width: 100%; text-align: left; border: none; border-bottom: 1px solid var(--line);
  background: transparent; padding: 12px 14px 12px 16px;
}
.row:hover { background: var(--raised); }
.row[aria-selected="true"] { background: var(--sea-soft); }
.row .wait { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
.row .wait.fresh { background: var(--wait-fresh); }
.row .wait.warm { background: var(--wait-warm); }
.row .wait.hot { background: var(--wait-hot); }
.row .line1 { display: flex; align-items: baseline; gap: 6px; min-width: 0; }
.row .who { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row .when { margin-left: auto; font-size: 12px; color: var(--muted); flex: none; font-variant-numeric: tabular-nums; }
.row .when.hot { color: var(--wait-hot); font-weight: 600; }
.row .when.warm { color: var(--wait-warm); font-weight: 600; }
.row .preview {
  color: var(--muted); font-size: 13px; margin-top: 1px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.row.unread .preview { color: var(--text); }
.row .line3 { display: flex; align-items: center; gap: 6px; margin-top: 6px; min-height: 20px; }
.row .site { font-size: 12px; color: var(--faint); }
.row .end { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; }
.badge {
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: var(--sea); color: var(--sea-ink); font-size: 12px; font-weight: 700;
  display: inline-grid; place-items: center; font-variant-numeric: tabular-nums;
}

.chip {
  display: inline-flex; align-items: center; gap: 4px; max-width: 140px;
  border-radius: 999px; padding: 1px 8px; font-size: 12px; line-height: 18px;
  background: var(--raised); color: var(--muted); border: 1px solid var(--line);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.chip.status-pending { color: var(--wait-warm); border-color: color-mix(in srgb, var(--wait-warm) 40%, var(--line)); }
.chip.status-open { color: var(--sea-text); border-color: color-mix(in srgb, var(--sea) 40%, var(--line)); }
.chip.status-resolved, .chip.status-closed { color: var(--muted); }
.chip.verified { color: var(--ok); border-color: color-mix(in srgb, var(--ok) 35%, var(--line)); }
.card .chip, .conv-head .chip { max-width: none; }
.chip button { border: none; background: none; padding: 0; color: inherit; line-height: 1; }

.avatar {
  border-radius: 50%; object-fit: cover; flex: none;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 650; line-height: 1;
}
.avatar.visitor { background: var(--raised); color: var(--muted); border: 1px solid var(--line); }

/* ------------------------------------------------------------ conversation */

.conv-head {
  display: flex; align-items: center; gap: 10px; padding: 10px 14px; min-height: 58px;
  border-bottom: 1px solid var(--line);
}
.conv-head .title { min-width: 0; }
.conv-head h2 { margin: 0; font-size: 15px; font-weight: 650; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.conv-head .sub { font-size: 12px; color: var(--muted); display: flex; gap: 8px; align-items: center; }
.conv-head .actions { margin-left: auto; display: flex; gap: 6px; align-items: center; }
.back { display: none; }

.thread {
  flex: 1; overflow-y: auto; padding: 16px 20px 20px; background: var(--canvas);
  display: flex; flex-direction: column;
}
.day { align-self: center; margin: 14px 0 8px; font-size: 12px; color: var(--muted); background: var(--surface); border: 1px solid var(--line); border-radius: 999px; padding: 2px 10px; }
.msg { display: flex; gap: 8px; align-items: flex-end; margin-top: 2px; }
.msg.first { margin-top: 10px; }
.msg.mine { flex-direction: row-reverse; }
.msg .slot { width: 28px; flex: none; }
.msg .col { max-width: min(68%, 560px); min-width: 0; display: flex; flex-direction: column; }
.msg.mine .col { align-items: flex-end; }
.msg .name { font-size: 12px; color: var(--muted); margin: 0 4px 2px; }
.bubble {
  padding: 8px 12px; border-radius: var(--radius-l);
  background: var(--surface); border: 1px solid var(--line);
  white-space: pre-wrap; overflow-wrap: anywhere;
}
.msg.mine .bubble { background: var(--sea); color: var(--sea-ink); border-color: transparent; }
.msg.mine .bubble a { color: inherit; }
.msg.note .col { max-width: min(80%, 640px); }
.msg.note .bubble { background: var(--note); border-color: var(--note-line); color: var(--text); }
/* Shown by the widget on its own, before anyone from the team was involved. */
.msg.bot .bubble { background: var(--sea-soft); color: var(--text); border: 1px dashed var(--sea); }
.msg.system { justify-content: center; }
.msg.system .bubble { background: transparent; border: none; color: var(--muted); font-size: 13px; text-align: center; }
.bubble.pending { opacity: 0.6; }
.note-tag { font-weight: 650; color: var(--wait-warm); margin-right: 6px; }
.msg .time { font-size: 11px; color: var(--faint); margin: 3px 4px 0; font-variant-numeric: tabular-nums; }
.typing { padding: 4px 22px 8px; font-size: 13px; color: var(--muted); background: var(--canvas); }

.attachments { display: flex; flex-wrap: wrap; gap: 6px; }
.bubble + .attachments { margin-top: 4px; }
.msg.mine .attachments { justify-content: flex-end; }
.thumb {
  padding: 0; border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden;
  background: var(--surface); cursor: zoom-in; display: block;
}
.thumb img { display: block; max-width: 240px; max-height: 240px; object-fit: cover; }
.progress { height: 4px; width: 160px; border-radius: 2px; background: var(--line); margin-top: 6px; overflow: hidden; }
.progress span { display: block; height: 100%; background: var(--sea); transition: width 0.2s; }

/* ---------------------------------------------------------------- composer */

.composer { border-top: 1px solid var(--line); background: var(--surface); padding: 8px 12px 12px; position: relative; }
.composer .modes { display: flex; gap: 2px; margin-bottom: 6px; }
.composer .modes button {
  border: none; background: none; padding: 4px 10px; border-radius: var(--radius-s); color: var(--muted); font-size: 13px;
}
.composer .modes button[aria-pressed="true"] { color: var(--text); background: var(--raised); font-weight: 600; }
.composer .modes button.note[aria-pressed="true"] { background: var(--note); }
.composer .box {
  display: flex; align-items: flex-end; gap: 6px; border: 1px solid var(--line-strong);
  border-radius: var(--radius-l); padding: 6px 6px 6px 8px; background: var(--surface);
}
.composer .box:focus-within { border-color: var(--sea); box-shadow: 0 0 0 3px var(--sea-soft); }
.composer.note .box { background: var(--note); border-color: var(--note-line); }
.composer .box.drop { border-style: dashed; border-color: var(--sea); }
.composer textarea {
  flex: 1; border: none; outline: none; resize: none; background: transparent;
  min-height: 36px; max-height: 180px; padding: 7px 4px;
}
.composer .foot { display: flex; justify-content: space-between; margin-top: 6px; font-size: 12px; color: var(--faint); }

.picker {
  position: absolute; left: 12px; right: 12px; bottom: calc(100% - 4px);
  background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius);
  box-shadow: var(--shadow-pop); max-height: 280px; overflow-y: auto; padding: 4px; z-index: 5;
}
.picker button {
  display: block; width: 100%; text-align: left; border: none; background: none; border-radius: var(--radius-s); padding: 7px 10px;
}
.picker button[aria-selected="true"] { background: var(--sea-soft); }
.picker .k { font-weight: 600; color: var(--sea-text); margin-right: 8px; }
.picker .b { display: block; color: var(--muted); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker .none { padding: 10px; color: var(--muted); font-size: 13px; }

/* ----------------------------------------------------------- context panel */

.context { overflow-y: auto; background: var(--canvas); padding: 12px; display: flex; flex-direction: column; gap: 10px; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 12px 14px; }
.card h3 { margin: 0 0 8px; font-size: 13px; font-weight: 650; }
.person { display: flex; gap: 10px; align-items: center; }
.person .name { font-weight: 650; font-size: 15px; overflow-wrap: anywhere; }
.person .mail { color: var(--muted); font-size: 13px; overflow-wrap: anywhere; display: flex; align-items: center; gap: 4px; }
.kv { display: grid; grid-template-columns: 96px minmax(0, 1fr); gap: 4px 10px; font-size: 13px; margin: 0; }
.kv dt { color: var(--muted); }
.kv dd { margin: 0; overflow-wrap: anywhere; }
.trail { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.trail li { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; font-size: 13px; }
.trail li:first-child a { font-weight: 600; }
.trail a { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-decoration: none; color: var(--text); }
.trail a:hover { color: var(--sea-text); text-decoration: underline; }
.trail .t { color: var(--faint); font-size: 12px; font-variant-numeric: tabular-nums; }
.past { list-style: none; margin: 0; padding: 0; }
.past button { width: 100%; text-align: left; border: none; background: none; padding: 6px 0; border-top: 1px solid var(--line); }
.past li:first-child button { border-top: none; }
.past .p { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.past .m { font-size: 12px; color: var(--muted); }
.stars { color: var(--wait-warm); letter-spacing: 1px; }

/* -------------------------------------------------------------------- menus */

.menu-wrap { position: relative; }
.menu {
  position: absolute; right: 0; top: calc(100% + 4px); z-index: 20; min-width: 220px;
  background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius);
  box-shadow: var(--shadow-pop); padding: 4px;
}
.menu button {
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
  border: none; background: none; border-radius: var(--radius-s); padding: 7px 10px;
}
.menu button:hover, .menu button:focus-visible { background: var(--raised); }
.menu button.danger { color: var(--danger); }
.menu button:disabled { color: var(--muted); cursor: not-allowed; background: none; }
.menu hr { border: none; border-top: 1px solid var(--line); margin: 4px 0; }
.menu .check { margin-left: auto; color: var(--sea-text); }
.tag-input { width: 100%; padding: 6px 8px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--raised); }

/* ---------------------------------------------------------- image viewer */

.viewer {
  position: fixed; inset: 0; z-index: 50; background: rgba(8, 11, 15, 0.84);
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; cursor: zoom-out;
}
.viewer img { max-width: 92vw; max-height: 82vh; border-radius: 8px; cursor: default; }
.viewer-bar { display: flex; align-items: center; gap: 8px; color: #fff; font-size: 14px; cursor: default; }

/* ------------------------------------------------------------ empty/alert */

.empty { display: grid; place-items: center; height: 100%; color: var(--muted); text-align: center; padding: 40px; }
.empty strong { display: block; color: var(--text); font-size: 15px; margin-bottom: 4px; }
.alert { padding: 9px 12px; border-radius: var(--radius); font-size: 13px; margin-top: 12px; background: var(--sea-soft); color: var(--sea-text); }
.alert.error { background: var(--danger-soft); color: var(--danger); }
.alert.warn { background: var(--note); color: var(--text); }
.toast { position: fixed; bottom: 18px; left: 50%; transform: translateX(-50%); z-index: 60; margin: 0; box-shadow: var(--shadow-pop); }

/* ----------------------------------------------------------- sign in page */

.auth { display: grid; place-items: center; min-height: 100vh; padding: 24px; }
.auth .card { width: 100%; max-width: 400px; padding: 28px; border-radius: var(--radius-l); box-shadow: var(--shadow-pop); }
.auth h1 { margin: 0 0 4px; font-size: 20px; }
.auth p.sub { margin: 0 0 18px; color: var(--muted); font-size: 14px; }
.auth label { display: block; font-size: 13px; color: var(--muted); margin: 12px 0 4px; }
.auth input { width: 100%; padding: 9px 12px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); }
.auth .btn { width: 100%; margin-top: 18px; padding: 9px 14px; }
.auth .qr { display: block; margin: 16px auto; border-radius: 8px; background: #fff; padding: 8px; }
.auth code, .settings code { background: var(--raised); padding: 2px 6px; border-radius: 6px; font-size: 13px; }

/* -------------------------------------------------------------- settings */

.settings-layout { display: grid; grid-template-columns: 230px minmax(0, 1fr); height: 100%; }
.subnav { background: var(--surface); border-right: 1px solid var(--line); padding: 16px 10px; overflow-y: auto; }
.subnav h1 { font-size: 16px; font-weight: 650; margin: 0 8px 14px; }
.subnav-group { display: flex; flex-direction: column; gap: 1px; margin-bottom: 16px; }
.subnav-title { font-size: 12px; color: var(--faint); margin: 0 8px 4px; }
.subnav button {
  text-align: left; border: none; background: none; border-radius: var(--radius-s); padding: 7px 10px; color: var(--text);
}
.subnav button:hover { background: var(--raised); }
.subnav button[aria-current="page"] { background: var(--sea-soft); color: var(--sea-text); font-weight: 600; }

.settings { height: 100%; overflow-y: auto; padding: 22px 28px 60px; }
.settings > * { max-width: 1000px; }
.settings-title { font-size: 20px; font-weight: 650; margin: 0 0 16px; }
.settings .panel {
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-l);
  padding: 18px 20px; margin-bottom: 14px;
}
.settings .panel h2 { margin: 0 0 6px; font-size: 15px; font-weight: 650; }
.settings .intro { margin: 0 0 12px; color: var(--muted); font-size: 13px; max-width: 70ch; }
.settings label { display: block; font-size: 13px; color: var(--muted); margin: 12px 0 0; }
.settings label > input, .settings label > select, .settings label > textarea, .settings label > .color-field { margin-top: 4px; }
.settings input[type="text"], .settings input[type="password"], .settings input[type="email"], .settings input[type="number"], .settings input[type="url"],
.settings input[type="date"], .settings input[type="time"], .settings textarea, .settings select {
  width: 100%; padding: 8px 11px; border-radius: var(--radius); border: 1px solid var(--line-strong);
  background: var(--surface); color: var(--text);
}
.settings input:disabled, .settings select:disabled, .settings textarea:disabled { background: var(--raised); color: var(--muted); }
.settings textarea { min-height: 80px; resize: vertical; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px; }
.settings textarea.plain { font-family: var(--font); font-size: 14px; }
.settings .site-picker { max-width: 320px; margin: 0 0 14px; }
.field-error { color: var(--danger); font-size: 13px; margin: 8px 0 0; }
.actions-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; }
.grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 16px; }
.grid-2 .span-2 { grid-column: 1 / -1; }
.grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 16px; }
.key-block { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--line); }
.key-block > label { margin-top: 0; }
.key-block code { overflow-wrap: anywhere; }
.key-previous { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; justify-content: space-between; margin-top: 10px; }
.key-heading { font-size: 14px; margin: 22px 0 4px; padding-top: 14px; border-top: 1px solid var(--line); }
.form-narrow { max-width: 420px; }
.form-narrow .btn { margin-top: 14px; }
.form-inline { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
.form-inline label { flex: 1; min-width: 200px; }

.settings .switch-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 10px 0; border-top: 1px solid var(--line); margin: 0; color: var(--text); cursor: pointer;
}
.settings .panel > .switch-row:first-of-type { border-top: none; }
.switch-row > span { display: flex; flex-direction: column; }
.switch-label { font-size: 14px; }
.switch-row.disabled { cursor: default; opacity: 0.7; }
.switch {
  appearance: none; -webkit-appearance: none; flex: none; width: 38px; height: 22px; border-radius: 11px;
  background: var(--line-strong); position: relative; cursor: pointer; margin: 0;
}
.switch::after {
  content: ""; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%;
  background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}
.switch:checked { background: var(--sea); }
.switch:checked::after { left: 19px; }
.switch:disabled { cursor: default; }
@media (prefers-reduced-motion: no-preference) {
  .switch, .switch::after { transition: background-color 0.15s, left 0.15s; }
}

.codes { display: grid; grid-template-columns: repeat(2, max-content); gap: 8px 20px; margin-top: 12px; }
.codes code { font-size: 15px; letter-spacing: 0.06em; }

.item-list, .quick-list { list-style: none; margin: 0; padding: 0; }
.item-list li { display: flex; gap: 14px; align-items: flex-start; padding: 12px 0; border-top: 1px solid var(--line); }
.item-list li:first-child { border-top: none; }
.item-list li.editing { display: block; }
.item-list li.muted .item-main { opacity: 0.55; }
.item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.item-main .chip { margin-left: 6px; }
.item-body { color: var(--text); font-size: 13px; white-space: pre-wrap; overflow-wrap: anywhere; }
.item-actions { display: flex; gap: 6px; align-items: center; flex: none; }
.settings .mini-switch { margin: 0; display: inline-flex; }

.quick-list li { display: flex; align-items: center; justify-content: space-between; padding: 6px 0; border-top: 1px solid var(--line); }
.quick-list li:first-child { border-top: none; }
.quick-actions { display: flex; gap: 2px; }

.team { display: flex; flex-direction: column; }
.member { display: flex; gap: 12px; align-items: flex-start; padding: 12px 0; border-top: 1px solid var(--line); }
.member:first-child { border-top: none; }
.member.inactive { opacity: 0.6; }
.member .who { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.member .tags { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
.member-actions { display: flex; gap: 6px; align-items: center; }
.settings .member-actions select { width: auto; padding: 5px 8px; }
.checks { display: flex; flex-wrap: wrap; gap: 6px 14px; margin-top: 6px; }
.settings .check-item { display: inline-flex; align-items: center; gap: 6px; margin: 0; color: var(--text); }

.settings .widget-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; max-width: 1120px; }
.widget-side { position: sticky; top: 0; }
.color-field { display: flex; align-items: center; gap: 10px; }
.color-field input[type="color"] { width: 44px; height: 34px; padding: 2px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--surface); }
.save-bar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 14px; }
.save-bar .alert { margin-top: 0; }
.save-bar.sticky { position: sticky; bottom: 0; background: var(--canvas); padding: 12px 0; border-top: 1px solid var(--line); }

/*
 * The customer's widget, drawn by its own stylesheet inside a shadow root
 * (components/WidgetMock.tsx). The stage is a scaled-down "page"; its
 * transform makes it the containing block for the widget's position: fixed.
 */
.wmock {
  display: block; position: relative; overflow: hidden; flex: none; pointer-events: none;
  border-radius: var(--radius-l); border: 1px solid var(--line);
  background: repeating-linear-gradient(45deg, var(--raised) 0 10px, var(--canvas) 10px 20px);
}
.wmock-stage { position: absolute; top: 0; left: 0; transform-origin: 0 0; }
.wmock-host { display: block; }

.check-line { display: flex !important; align-items: center; gap: 8px; color: var(--text) !important; }
.check-line input { margin: 0; }
.preset-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(146px, 1fr)); gap: 10px; margin-top: 12px; }
.preset-card {
  display: flex; flex-direction: column; align-items: stretch; gap: 6px; text-align: left;
  padding: 8px; border: 1px solid var(--line); border-radius: var(--radius-l); background: var(--surface); color: var(--text);
}
.preset-card .wmock { align-self: center; border-radius: var(--radius); }
.preset-card:hover:not(:disabled) { border-color: var(--line-strong); }
.preset-card.on { border-color: var(--sea); box-shadow: 0 0 0 2px var(--sea-soft); }
.preset-card:focus-visible { outline: 2px solid var(--sea); outline-offset: 2px; }
.preset-name { display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 13px; }
.preset-desc { font-size: 12px; line-height: 1.4; color: var(--muted); }
.preset-swatch { width: 10px; height: 10px; border-radius: 50%; flex: none; box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15); display: inline-block; }
.preset-apply { margin-top: 12px; display: inline-flex; align-items: center; gap: 8px; }
.range-field { margin: 12px 0 0; font-size: 13px; color: var(--muted); display: flex; flex-direction: column; gap: 6px; }
.range-field input[type="range"] { width: 100%; accent-color: var(--sea); }
.range-field .check-line { margin: 0; }

.week { margin-top: 12px; display: flex; flex-direction: column; }
.day-row { display: grid; grid-template-columns: 140px minmax(0, 1fr); gap: 10px; align-items: center; padding: 8px 0; border-top: 1px solid var(--line); }
.day-row:first-child { border-top: none; }
.settings .day-toggle { display: inline-flex; align-items: center; gap: 8px; margin: 0; color: var(--text); }
.ranges { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.range { display: inline-flex; align-items: center; gap: 6px; }
.settings .range input[type="time"] { width: 116px; padding: 6px 8px; }
.chips-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }

.snippet {
  background: var(--raised); border: 1px solid var(--line); border-radius: var(--radius);
  padding: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px; overflow-x: auto; white-space: pre; margin-top: 6px;
}
.profile-row { display: flex; align-items: center; gap: 16px; }
.profile-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.profile-name { display: flex; gap: 10px; align-items: flex-end; max-width: 520px; }
.profile-name label { flex: 1; }

@media (max-width: 1100px) {
  .settings .widget-layout { grid-template-columns: 1fr; }
  .widget-side { position: static; }
}

@media (max-width: 760px) {
  .settings-layout { grid-template-columns: 1fr; grid-template-rows: auto minmax(0, 1fr); }
  .subnav { border-right: none; border-bottom: 1px solid var(--line); padding: 10px; display: flex; gap: 12px; overflow-x: auto; }
  .subnav h1, .subnav-title { display: none; }
  .subnav-group { flex-direction: row; margin: 0; }
  .subnav button { white-space: nowrap; }
  .settings { padding: 16px 14px 60px; }
  .grid-2, .grid-3 { grid-template-columns: 1fr; }
  .day-row { grid-template-columns: 1fr; }
  .member { flex-wrap: wrap; }
}

/* ------------------------------------------------------ language & toggles */

.lang-switch { display: flex; justify-content: flex-end; gap: 2px; margin: -10px -10px 8px 0; }
.lang-switch button {
  border: 1px solid transparent; background: none; color: var(--muted); border-radius: var(--radius-s);
  padding: 2px 8px; font-size: 12px; font-weight: 600;
}
.lang-switch button[aria-pressed="true"] { color: var(--sea-text); border-color: var(--line); background: var(--raised); }

.segmented { display: inline-flex; background: var(--raised); border: 1px solid var(--line); border-radius: var(--radius); padding: 2px; gap: 2px; }
.segmented button { border: none; background: none; border-radius: 8px; padding: 6px 12px; font-size: 13px; color: var(--muted); }
.segmented button[aria-checked="true"] { background: var(--surface); color: var(--text); font-weight: 600; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08); }

/* ----------------------------------------------------------------- history */

.filters-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 8px; margin-top: 10px; }
.filters-grid label { display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: var(--muted); }
.filters-grid input, .filters-grid select {
  width: 100%; padding: 5px 8px; border-radius: var(--radius-s); border: 1px solid var(--line); background: var(--raised); font-size: 13px;
}
.readonly-bar { border-top: 1px solid var(--line); background: var(--surface); padding: 12px 16px; color: var(--muted); font-size: 13px; }

/* ------------------------------------------------------------------- stats */

.stats-page { height: 100%; overflow-y: auto; padding: 22px 28px 60px; }
.stats-page > * { max-width: 1180px; }
.stats-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.stats-head h1 { font-size: 20px; font-weight: 650; margin: 0; }
.stats-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.stats-filters select { padding: 7px 10px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); }

.tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
.tile { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-l); padding: 14px 16px; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.tile-label { font-size: 13px; color: var(--muted); }
.tile-value { font-size: 26px; font-weight: 650; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
.tile-sub { font-size: 12px; color: var(--faint); }

.stats-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.stats-grid .card { position: relative; }
.stats-grid .card.wide { grid-column: 1 / -1; }
.table-toggle { position: absolute; top: 10px; right: 10px; }

.bars { display: flex; align-items: stretch; gap: 2px; height: 190px; padding-top: 22px; }
.bar-col { flex: 1; min-width: 0; display: flex; flex-direction: column; cursor: default; }
.bar-area { position: relative; flex: 1; display: flex; align-items: flex-end; justify-content: center; border-bottom: 1px solid var(--line-strong); }
.bar { width: min(28px, 70%); border-radius: 4px 4px 0 0; background: color-mix(in srgb, var(--sea) 75%, var(--surface)); }
.bar-col.on .bar { background: var(--sea); }
.bar-col.on .bar-area { background: var(--raised); }
.bar-value { position: absolute; top: -20px; left: 50%; transform: translateX(-50%); font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.bar-tick { height: 20px; padding-top: 4px; font-size: 11px; color: var(--muted); text-align: center; white-space: nowrap; overflow: visible; }
.chart-wrap { width: 100%; }
svg.chart { width: 100%; height: auto; display: block; overflow: visible; }
svg.chart .axis { stroke: var(--line-strong); stroke-width: 1; }
svg.chart .tick { fill: var(--muted); font-size: 11px; }
svg.chart .value { fill: var(--text); font-size: 12px; font-weight: 600; }

.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th, .data-table td { text-align: left; padding: 7px 8px; border-bottom: 1px solid var(--line); }
.data-table th { color: var(--muted); font-weight: 600; }
.data-table .num { text-align: right; font-variant-numeric: tabular-nums; }
.who-cell { display: inline-flex; align-items: center; gap: 8px; }

.heat-wrap { overflow-x: auto; }
.heat { display: grid; grid-template-columns: 44px repeat(24, minmax(14px, 1fr)); gap: 2px; min-width: 460px; }
.heat-hour { font-size: 11px; color: var(--muted); text-align: left; }
.heat-day { font-size: 12px; color: var(--muted); align-self: center; }
.heat-cell { height: 20px; border-radius: 4px; }

.hbars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.hbars li { display: grid; grid-template-columns: 90px minmax(0, 1fr) 36px; gap: 8px; align-items: center; font-size: 13px; }
.hbar-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hbar-track { height: 10px; background: var(--raised); border-radius: 5px; overflow: hidden; }
.hbar { display: block; height: 100%; border-radius: 5px; min-width: 2px; }
.hbar-n { text-align: right; color: var(--muted); }

.comments { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.comments li { display: flex; flex-direction: column; gap: 2px; padding-bottom: 10px; border-bottom: 1px solid var(--line); }
.comments li:last-child { border-bottom: none; }
.comments q { font-size: 14px; }

@media (max-width: 1100px) {
  .tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
  .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .stats-grid { grid-template-columns: 1fr; }
  .stats-page { padding: 16px 14px 60px; }
}

/* --------------------------------------------------------------- layouts */

.info-toggle { display: none; }
.scrim { display: none; }

@media (max-width: 1200px) {
  .inbox { grid-template-columns: 300px minmax(0, 1fr); }
  .info-toggle { display: inline-flex; }
  .context {
    position: fixed; top: 0; right: 0; bottom: 0; width: min(340px, 92vw); z-index: 30;
    border-left: 1px solid var(--line); box-shadow: var(--shadow-pop);
    transform: translateX(100%); visibility: hidden;
  }
  .context.open { transform: none; visibility: visible; }
  .scrim.open { display: block; position: fixed; inset: 0; z-index: 25; background: rgba(8, 11, 15, 0.35); }
}

@media (max-width: 760px) {
  .shell { grid-template-columns: 1fr; grid-template-rows: minmax(0, 1fr) 56px; }
  .rail { grid-row: 2; flex-direction: row; justify-content: space-around; padding: 0 8px; }
  .rail .brand, .rail .spacer { display: none; }
  .rail button[aria-current="page"]::before { left: 10px; right: 10px; top: auto; bottom: -6px; width: auto; height: 3px; border-radius: 3px 3px 0 0; }
  .view { height: calc(100vh - 56px); }
  .inbox { grid-template-columns: 1fr; }
  .inbox.has-selection .list-pane { display: none; }
  .inbox:not(.has-selection) .conv-pane { display: none; }
  .back { display: inline-flex; }
  .hide-sm { display: none; }
  .conv-head { gap: 6px; padding: 8px 10px; }
  .conv-head .sub { flex-wrap: nowrap; overflow: hidden; }
  .conv-head .actions { gap: 2px; }
  .msg .col { max-width: 85%; }
}

@media (prefers-reduced-motion: no-preference) {
  .context { transition: transform 0.18s ease, visibility 0.18s; }
}
@media (prefers-reduced-motion: reduce) {
  .progress span { transition: none; }
}

/* ------------------------------------------------------------------ v3: teamwork */

/* Saved replies that fit the customer's last message, above an empty box. */
.suggest { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 0 8px; }
.suggest-chip {
  display: inline-flex; align-items: center; gap: 6px; max-width: 100%;
  border: 1px solid var(--line-strong); background: var(--surface); color: var(--text);
  border-radius: 999px; padding: 4px 12px; font: inherit; font-size: 13px; cursor: pointer;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.suggest-chip .k { color: var(--sea-text); font-weight: 600; }
.suggest-chip:hover, .suggest-chip:focus-visible { border-color: var(--sea); background: var(--sea-soft); }

/* What the visitor is typing before they send it: quoted, never mistaken for a message. */
.typing.draft { display: flex; flex-direction: column; gap: 2px; }
.typing.draft q {
  quotes: none; font-style: italic; color: var(--text); opacity: .75;
  border-left: 2px dashed var(--line-strong); padding-left: 8px;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; white-space: pre-wrap;
}

.chip.ticket { color: var(--sea-text); border-color: color-mix(in srgb, var(--sea) 40%, var(--line)); }

/* The hand-over dialog. */
.dialog-scrim {
  position: fixed; inset: 0; z-index: 55; background: rgba(8, 11, 15, 0.45);
  display: grid; place-items: center; padding: 16px;
}
.dialog {
  width: min(440px, 100%); background: var(--surface); color: var(--text);
  border-radius: var(--radius-l); box-shadow: var(--shadow-pop); padding: 20px;
  display: flex; flex-direction: column; gap: 12px;
}
.dialog h2 { margin: 0; font-size: 17px; }
.dialog label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; font-weight: 600; }
.dialog .hint { font-weight: 400; }
.dialog textarea { width: 100%; box-sizing: border-box; resize: vertical; }
.dialog-actions { display: flex; justify-content: flex-end; gap: 8px; }

/* Settings: FAQ list, auto-assign choice, audit log. */
.faq-list { list-style: none; margin: 0 0 12px; padding: 0; }
.faq-list li { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding: 8px 0; border-top: 1px solid var(--line); }
.faq-list li:first-child { border-top: none; }
.faq-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.faq-text span { color: var(--muted); font-size: 13px; white-space: pre-wrap; overflow-wrap: anywhere; }
.wprev-faq { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.wprev-faq span { border: 1px solid #dce1e7; background: #fff; color: #18202b; border-radius: 8px; padding: 5px 8px; font-size: 12px; }
.radio-list { display: flex; flex-direction: column; gap: 8px; }
.table-wrap { overflow-x: auto; margin: 8px 0 12px; }
.data-table.audit td { vertical-align: top; }
.data-table.early-access td { vertical-align: top; }
.data-table.early-access select { padding: 5px 6px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); font-size: 13px; }
.early-access-message { max-width: 320px; white-space: pre-wrap; margin: 4px 0 0; color: var(--muted); }
.data-table .nowrap { white-space: nowrap; }
.hint.block { display: block; }

/* ------------------------------------------------- plans, billing, customers */

.chip.plan-chip { background: var(--note); color: var(--text); }
.chip.block { display: inline-block; margin-top: 4px; }
.hint.error { color: var(--danger); }
.plan-headline { margin: 0 0 6px; font-size: 18px; display: flex; align-items: center; gap: 8px; }
.settings .hbars { margin-top: 8px; }
.empty.suspended { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; }
.empty.suspended p { margin: 0; color: var(--muted); }
.data-table.customers td { vertical-align: top; }
.data-table.customers select { padding: 5px 6px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); font-size: 13px; max-width: 120px; }
.approve-form { display: flex; flex-direction: column; gap: 6px; min-width: 190px; }
.approve-form label { display: flex; flex-direction: column; gap: 3px; font-size: 13px; }
.approve-form select, .approve-form input { padding: 5px 6px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); font-size: 13px; }

/* ------------------------------------------------ 2026-09-30: console gaps */

.btn.primary.danger { background: var(--danger); color: #fff; }

/* A text-only action under a form's main button: "forgot your password?". */
.link-btn {
  display: block; width: 100%; margin-top: 12px; padding: 4px; border: none; background: none;
  color: var(--sea-text); font-size: 13px; text-align: center;
}
.link-btn:hover { text-decoration: underline; }

/* The console's own confirm and form dialogs (components/Dialog.tsx). */
.dialog-body { margin: 0; color: var(--muted); font-size: 14px; font-weight: 400; white-space: pre-line; }
.dialog label > span { display: block; }
.dialog input[type="text"], .dialog input[type="email"], .dialog input[type="password"], .dialog textarea {
  width: 100%; padding: 8px 11px; border-radius: var(--radius); border: 1px solid var(--line-strong);
  background: var(--surface); color: var(--text); font-weight: 400;
}
.dialog textarea { min-height: 96px; }

/*
 * "Connection lost" sits above the screen rather than over it: laid over the
 * top it would cover the inbox's own header, and pushing a 100%-high screen
 * down would cut the reply box off the bottom instead.
 */
.view { display: flex; flex-direction: column; }
.view > * { min-height: 0; }
.view > :not(.conn-banner) { flex: 1 1 0; }
.conn-banner {
  flex: none; padding: 7px 14px; text-align: center; font-size: 13px;
  background: var(--note); color: var(--text); border-bottom: 1px solid var(--note-line);
}

/* Inbox: title row, site and tag filters, ticking rows for a bulk action. */
.list-title { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.list-title h1 { margin: 0; }
.list-filters { display: flex; gap: 6px; margin-top: 6px; }
.list-filters select, .list-filters input {
  flex: 1; min-width: 0; padding: 5px 8px; border-radius: var(--radius-s);
  border: 1px solid var(--line); background: var(--raised); font-size: 13px;
}
.bulk-bar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 8px 12px;
  border-bottom: 1px solid var(--line); background: var(--sea-soft); font-size: 13px;
}
.bulk-count { font-weight: 600; color: var(--sea-text); }
.bulk-actions { display: inline-flex; flex-wrap: wrap; gap: 6px; margin-left: auto; }
.row .pick {
  width: 22px; height: 22px; margin: 7px; border-radius: 6px; border: 1.5px solid var(--line-strong);
  display: grid; place-items: center; background: var(--surface); color: var(--sea-ink);
}
.row .pick.on { background: var(--sea); border-color: var(--sea); }
.row.picked { background: var(--sea-soft); }
.load-more { padding: 12px; text-align: center; }

/* Thread: older pages, a message's own actions, documents. */
.earlier { align-self: center; margin-bottom: 8px; }
.bubble-row { display: flex; align-items: center; gap: 4px; max-width: 100%; }
.bubble-col { min-width: 0; display: flex; flex-direction: column; }
.msg.mine .bubble-col { align-items: flex-end; }
.msg-actions { display: inline-flex; gap: 2px; flex: none; opacity: 0; }
.msg:hover .msg-actions, .msg-actions:focus-within { opacity: 1; }
/* No hover on a touch screen, so there the actions simply stay visible. */
@media (hover: none) { .msg-actions { opacity: 1; } }
.msg-actions button {
  border: none; background: none; color: var(--faint); padding: 5px; border-radius: 6px;
  display: grid; place-items: center;
}
.msg-actions button:hover, .msg-actions button:focus-visible { color: var(--text); background: var(--surface); }
.file-chip {
  display: inline-flex; align-items: center; gap: 8px; max-width: 280px; padding: 8px 12px;
  border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface);
  color: var(--text); text-decoration: none; font-size: 13px;
}
.file-chip:hover { border-color: var(--sea); }
.file-chip .file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-chip .hint { flex: none; }

.picker.emoji-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 2px; right: auto; width: min(340px, calc(100% - 24px)); }
.picker.emoji-grid button { text-align: center; font-size: 20px; line-height: 1.2; padding: 5px 0; }
.picker.emoji-grid button:hover { background: var(--raised); }

.chip.live { color: var(--ok); border-color: color-mix(in srgb, var(--ok) 35%, var(--line)); }

/* A link among menu buttons, and the items that only exist where space is short. */
.menu .menu-link {
  display: flex; align-items: center; gap: 8px; border-radius: var(--radius-s); padding: 7px 10px;
  color: var(--text); text-decoration: none;
}
.menu .menu-link:hover, .menu .menu-link:focus-visible { background: var(--raised); }
.menu button.show-sm { display: none; }

/* Visitors, customers, audit filters, the statistics range. */
.head-count { margin-left: 10px; font-weight: 400; }
.empty-card { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
.data-table.visitors td { vertical-align: top; }
.data-table.visitors .page-cell { max-width: 360px; overflow-wrap: anywhere; }
.page-search {
  min-width: 260px; padding: 7px 10px; border-radius: var(--radius);
  border: 1px solid var(--line-strong); background: var(--surface);
}
.new-customer { margin-bottom: 14px; }
.new-customer-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.new-customer-grid label { display: flex; flex-direction: column; gap: 3px; font-size: 13px; color: var(--muted); }
.new-customer-grid input, .new-customer-grid select {
  padding: 7px 10px; border-radius: var(--radius); border: 1px solid var(--line-strong);
  background: var(--surface); color: var(--text);
}
.audit-filters { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)) auto; gap: 10px; align-items: end; margin-bottom: 12px; }
.settings .audit-filters label { margin: 0; }
.range-date { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--muted); }
.range-date input {
  padding: 6px 8px; border-radius: var(--radius); border: 1px solid var(--line-strong);
  background: var(--surface); color: var(--text);
}
.member-actions { flex-wrap: wrap; justify-content: flex-end; }

@media (max-width: 760px) {
  .menu button.show-sm { display: flex; }
  /* One more screen than fits across a phone: the rail scrolls rather than squeezing. */
  .rail { overflow-x: auto; justify-content: flex-start; scrollbar-width: none; }
  .rail button { flex: 1 0 44px; }
  .new-customer-grid, .audit-filters { grid-template-columns: 1fr; }
  .page-search { min-width: 0; width: 100%; }
}

/* A dialog opened from a width-capped page must still cover the whole screen. */
.settings > .dialog-scrim, .stats-page > .dialog-scrim { max-width: none; }


/* ======================================================================
 * Aura (2026-09-30): every region floats as its own soft card on a quiet
 * canvas, the rail is a column of its own, and the accent is a teal
 * gradient. Written as a layer over the rules above rather than into them,
 * so the older, flatter rules still describe the structure and these only
 * the finish. The dark palette is redefined here too, and wins over the one
 * at the top of the file because it comes later.
 * ==================================================================== */
:root {
  --canvas: #eef2f6; --surface: #ffffff; --raised: #f4f7fa; --line: #e5e9ef; --line-strong: #d3dae3;
  --text: #0f1729; --muted: #5b6878; --faint: #8a95a4;
  --sea: #0d9488; --sea-ink: #ffffff; --sea-soft: #e3f5f2; --sea-text: #0f766e;
  --rail: #ffffff; --rail-text: #7a8697; --rail-hover: #f1f5f9;
  --radius-s: 8px; --radius: 12px; --radius-l: 16px;
  --grad: linear-gradient(135deg, #14b8a6 0%, #0d9488 45%, #0e7490 100%);
  --lift: 0 1px 2px rgba(15, 23, 41, 0.05), 0 12px 32px -18px rgba(15, 23, 41, 0.22);
}
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --canvas: #0b1016; --surface: #131a23; --raised: #1a232e; --line: #222c39; --line-strong: #2f3b4b;
    --text: #e8eef5; --muted: #9aa7b6; --faint: #6c7989;
    --sea: #14b8a6; --sea-ink: #032320; --sea-soft: rgba(20, 184, 166, 0.14); --sea-text: #5eead4;
    --rail: #131a23; --rail-text: #7d8a9a; --rail-hover: #1a232e;
    --lift: 0 1px 2px rgba(0, 0, 0, 0.3), 0 12px 32px -18px rgba(0, 0, 0, 0.7);
  }
}
:root[data-theme="dark"] {
  --canvas: #0b1016; --surface: #131a23; --raised: #1a232e; --line: #222c39; --line-strong: #2f3b4b;
  --text: #e8eef5; --muted: #9aa7b6; --faint: #6c7989;
  --sea: #14b8a6; --sea-ink: #032320; --sea-soft: rgba(20, 184, 166, 0.14); --sea-text: #5eead4;
  --rail: #131a23; --rail-text: #7d8a9a; --rail-hover: #1a232e;
  --lift: 0 1px 2px rgba(0, 0, 0, 0.3), 0 12px 32px -18px rgba(0, 0, 0, 0.7);
}

/* shell */
:root .shell { background: var(--canvas); }
@media (min-width: 761px) { :root .shell { grid-template-columns: 84px minmax(0, 1fr); } }
:root .rail { margin: 12px 0 12px 12px; border-radius: 22px; box-shadow: var(--lift); padding: 14px 0; gap: 6px; }
:root .rail .brand { background: var(--grad); border-radius: 14px; width: 40px; height: 40px; box-shadow: 0 6px 16px -6px rgba(13, 148, 136, 0.7); }
:root .rail button { border-radius: 14px; }
:root .rail button:hover { color: var(--text); }
:root .rail button[aria-current="page"] { background: var(--sea-soft); color: var(--sea-text); }
:root .rail button[aria-current="page"]::before { display: none; }
:root .rail .presence { border-color: var(--rail); }
:root .view { padding: 12px; box-sizing: border-box; }
:root .btn { border-radius: 10px; border-color: var(--line-strong); }
:root .btn.primary { background: var(--grad); box-shadow: 0 4px 12px -4px rgba(13, 148, 136, 0.55); }
:root .chip { border-radius: 8px; }
:root .badge { background: var(--grad); }

/* inbox */
:root .inbox { gap: 12px; }
@media (min-width: 1201px) { :root .inbox { grid-template-columns: 330px minmax(0, 1fr) 320px; } }
:root .pane { border-radius: 22px; box-shadow: var(--lift); overflow: hidden; }
:root .pane + .pane { border-left: none; }
:root .list-head { padding: 18px 16px 12px; border-bottom: none; }
:root .list-head h1, :root .list-title { font-size: 19px; letter-spacing: -0.015em; }
:root .tabs button { border-radius: 999px; padding: 5px 11px; }
:root .search input { background: var(--raised); border-color: transparent; border-radius: 999px; padding-top: 9px; padding-bottom: 9px; }
:root .search input:focus { background: var(--surface); border-color: var(--sea); outline: none; box-shadow: 0 0 0 4px var(--sea-soft); }
:root .rows { padding: 2px 8px 12px; }
:root .row { border: none; border-radius: 16px; padding: 12px 12px 12px 18px; margin-bottom: 2px; }
:root .row .wait { left: 7px; top: 14px; bottom: 14px; width: 4px; border-radius: 4px; }
:root .row[aria-selected="true"] { background: var(--sea-soft); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--sea) 28%, transparent); }
:root .row .who { font-weight: 650; }
:root .avatar.visitor { background: linear-gradient(145deg, var(--sea-soft), var(--raised)); color: var(--sea-text); border-color: transparent; font-weight: 700; }
:root .conv-head { padding: 14px 20px; min-height: 66px; border-bottom: 1px solid var(--line); }
:root .conv-head h2 { font-size: 16.5px; letter-spacing: -0.01em; }
:root .thread { background: linear-gradient(180deg, var(--raised), var(--surface)); padding: 20px 26px 24px; }
:root .day { border: none; background: transparent; color: var(--faint); font-weight: 650; letter-spacing: 0.06em; text-transform: uppercase; font-size: 10.5px; }
:root .bubble { border: none; box-shadow: 0 1px 2px rgba(15, 23, 41, 0.07), 0 2px 10px -6px rgba(15, 23, 41, 0.15); border-radius: 20px; padding: 10px 15px; line-height: 1.45; }
:root .msg:not(.mine):not(.system) .bubble { border-bottom-left-radius: 6px; }
:root .msg.mine .bubble { border-bottom-right-radius: 6px; background: var(--grad); color: #fff; box-shadow: 0 6px 16px -8px rgba(13, 148, 136, 0.8); }
:root .msg.note .bubble { box-shadow: none; border: 1px solid var(--note-line); }
:root .msg.system .bubble { box-shadow: none; }
:root .msg .name { margin-bottom: 4px; }
:root .typing { background: transparent; }
:root .composer { border-top: 1px solid var(--line); padding: 10px 18px 14px; }
:root .composer .box { border-radius: 22px; border-color: var(--line); background: var(--raised); padding: 6px 6px 6px 10px; }
:root .composer .box:focus-within { background: var(--surface); box-shadow: 0 0 0 4px var(--sea-soft); }
:root .composer .btn.primary { border-radius: 999px; padding: 8px 18px; }
:root .composer .modes button { border-radius: 999px; }
:root .context { background: var(--surface); gap: 0; padding: 6px 0; }
:root .context .card { border: none; border-radius: 0; padding: 16px 20px; border-bottom: 1px solid var(--line); }
:root .context .card:last-child { border-bottom: none; }
:root .card h3 { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--faint); margin-bottom: 10px; }

/* settings, stats, lists */
:root .settings-layout { gap: 12px; }
:root .subnav { border: none; border-radius: 22px; box-shadow: var(--lift); padding: 18px 12px; }
:root .subnav button { border-radius: 12px; padding: 8px 12px; }
:root .subnav button[aria-current="page"] { background: var(--sea-soft); }
:root .settings, :root .stats-page { padding: 14px 20px 60px; }
:root .settings-title, :root .stats-head h1 { font-size: 24px; letter-spacing: -0.02em; }
:root .settings .panel, :root .stats-grid .card, :root .tile { border: none; border-radius: 20px; box-shadow: var(--lift); padding: 20px 22px; }
:root .tile { position: relative; overflow: hidden; }
:root .tile::before { content: ""; position: absolute; left: 0; top: 0; right: 0; height: 3px; background: var(--grad); opacity: 0.8; }
:root .tile-value { font-size: 30px; letter-spacing: -0.03em; }
:root .settings input[type]:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]), :root .settings select, :root .settings textarea { border-radius: 12px; border-color: var(--line); background: var(--raised); }
:root .settings input:focus, :root .settings select:focus, :root .settings textarea:focus { outline: none; border-color: var(--sea); background: var(--surface); box-shadow: 0 0 0 4px var(--sea-soft); }
:root .segmented { border-radius: 999px; border: none; padding: 3px; }
:root .segmented button { border-radius: 999px; }
:root .switch:checked { background: var(--grad); }
:root .dialog { border-radius: 22px; }
:root .list-pane .readonly-bar { border-top-color: var(--line); }
@media (max-width: 760px) {
  :root .view { padding: 0; }
  :root .inbox { gap: 0; }
  :root .pane, :root .subnav { border-radius: 0; box-shadow: none; }
  :root .rail { margin: 0; border-radius: 0; padding: 0 8px; box-shadow: 0 -1px 0 var(--line); }
}

/* ------------------------------------------------------------ sites page */

.sites-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: -6px 0 14px; }
.sites-count { margin: 0; color: var(--muted); font-size: 13px; }
.sites-top-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.sites-mark {
  flex: none; width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center;
  font-weight: 750; font-size: 17px; box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}
.pill { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 2px 10px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.pill::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.pill.on { background: color-mix(in srgb, var(--ok) 13%, transparent); color: var(--ok); }
.pill.off { background: var(--raised); color: var(--muted); }
.pill.warn { background: color-mix(in srgb, var(--wait-warm) 15%, transparent); color: var(--wait-warm); }
.chip.warn-chip { color: var(--wait-warm); border-color: color-mix(in srgb, var(--wait-warm) 40%, var(--line)); }
.sites-meta { display: block; font-size: 12.5px; color: var(--muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sites-domains { display: inline-flex; flex-wrap: wrap; gap: 4px; }

/* The selected site, at the top of its settings. */
.sites-head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.sites-head .sites-mark { width: 48px; height: 48px; font-size: 20px; border-radius: 14px; }
.sites-head-text { flex: 1; min-width: 200px; }
.sites-head-text h2 { margin: 0 0 4px; font-size: 19px; letter-spacing: -0.01em; }
.sites-head-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sites-head-key { display: flex; flex-direction: column; gap: 4px; min-width: 0; max-width: 100%; }

/* One setting: text on the left, the control on the right. */
.setting { display: grid; grid-template-columns: minmax(0, 290px) minmax(0, 1fr); gap: 8px 28px; padding: 16px 0; border-top: 1px solid var(--line); align-items: start; }
.setting:first-child, .intro + .setting, .alert + .setting { border-top: none; }
.setting.wide { grid-template-columns: 1fr; }
.setting-text label, .setting-title { display: block; margin: 0; font-size: 14px; font-weight: 600; color: var(--text); }
.setting-text .hint { margin: 3px 0 0; line-height: 1.45; }
.setting-control { min-width: 0; display: flex; align-items: center; }
.setting-control > input, .setting-control > textarea { margin: 0; }
.setting.wide .setting-control { display: block; }
.stack { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; width: 100%; }
.stack .actions-row { margin-top: 0; }
.settings .suffix-field { display: flex; align-items: center; gap: 8px; }
.settings .suffix-field input { width: 120px; }
.suffix-field span { color: var(--muted); font-size: 13px; }
.meter { width: 100%; max-width: 360px; height: 8px; border-radius: 999px; background: var(--raised); overflow: hidden; box-shadow: inset 0 0 0 1px var(--line); }
.meter span { display: block; height: 100%; border-radius: inherit; background: var(--sea); }
.meter.warn span { background: var(--wait-warm); }
.sites-secret { font-size: 14px; background: var(--raised); padding: 4px 10px; border-radius: 8px; }
.sites-anon .grid-3 label { margin-top: 0; }
.sites-anon .actions-row { margin-top: 10px; }

/* Something to copy. */
.copy-box { display: flex; align-items: stretch; width: 100%; border: 1px solid var(--line); border-radius: var(--radius); background: var(--raised); overflow: hidden; }
.copy-box code {
  flex: 1; min-width: 0; padding: 10px 12px; font-size: 12.5px; background: none; border-radius: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; overflow-x: auto; white-space: nowrap;
}
.copy-box .btn { border: none; border-left: 1px solid var(--line); border-radius: 0; background: var(--surface); flex: none; }
.copy-box.compact code { padding: 6px 10px; font-size: 12px; max-width: 280px; }
.copy-box.compact .btn { padding: 4px 10px; }

.danger-box { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; border: 1px solid color-mix(in srgb, var(--danger) 35%, var(--line)); background: color-mix(in srgb, var(--danger) 5%, var(--surface)); border-radius: var(--radius); padding: 14px 16px; margin-top: 12px; }
.danger-box > div { flex: 1; min-width: 220px; }
.danger-box .hint { margin: 4px 0 0; }
.danger-box .btn.danger { border-color: color-mix(in srgb, var(--danger) 45%, var(--line)); }

.sites-empty { text-align: center; padding: 40px 20px; }
.form-dialog { width: min(520px, 100%); }
.form-dialog.wide { width: min(640px, 100%); }
.form-dialog input, .form-dialog textarea { padding: 8px 11px; border-radius: var(--radius); border: 1px solid var(--line-strong); background: var(--surface); color: var(--text); font-weight: 400; }

/* A strip of sites, then the chosen one in tabs. */
.sites-strip { display: flex; gap: 10px; overflow-x: auto; padding: 2px 2px 12px; }
.sites-chipcard {
  flex: 0 0 auto; min-width: 220px; max-width: 280px; display: flex; align-items: center; gap: 12px; text-align: left;
  border: 1px solid var(--line); background: var(--surface); border-radius: var(--radius-l); padding: 12px 14px; color: var(--text);
}
.sites-chipcard:hover { border-color: var(--line-strong); }
.sites-chipcard[aria-selected="true"] { border-color: var(--sea); box-shadow: 0 0 0 3px var(--sea-soft); }
.sites-chipcard-text { min-width: 0; display: flex; flex-direction: column; }
.tabs-line { display: flex; gap: 2px; margin: 18px -22px 0; padding: 0 16px; border-bottom: 1px solid var(--line); overflow-x: auto; overflow-y: hidden; scrollbar-width: none; }
.tabs-line button { border: none; background: none; padding: 10px 12px; font-size: 14px; color: var(--muted); border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; }
.tabs-line button:hover { color: var(--text); }
.tabs-line button[aria-selected="true"] { color: var(--text); font-weight: 650; border-bottom-color: var(--sea); }
.tabs-line button.danger[aria-selected="true"] { border-bottom-color: var(--danger); color: var(--danger); }
.sites-body { padding-top: 8px; }
.sites-body > .intro { margin: 10px 0 0; }

@media (max-width: 900px) {
  .setting { grid-template-columns: 1fr; gap: 8px; }
  .sites-head-key { width: 100%; }
  .copy-box.compact code { max-width: none; }
}

/* ------------------------------------------------ settings: shared layout */

.panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.panel-head-text { flex: 1; min-width: 0; }
.panel-head-text .intro { margin-bottom: 4px; }
.panel-head-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: none; }
.settings .panel { padding: 22px 24px; }
.settings .panel h2 { font-size: 16px; }
.settings .panel.is-danger { box-shadow: 0 0 0 1px color-mix(in srgb, var(--danger) 30%, transparent), var(--lift, none); }
.settings .panel.is-danger h2 { color: var(--danger); }
.panel-head + .setting, .panel-head + form > .setting:first-child, .panel-head + div > .setting:first-child { border-top: none; }
.settings label.setting { display: grid; margin: 0; color: var(--text); cursor: pointer; font-size: 14px; }
.settings label.setting.disabled { cursor: default; opacity: 0.7; }
.setting-control .switch { margin-left: auto; }
.setting:not(.wide) .setting-control:has(> .switch:only-child) { justify-content: flex-end; }
.settings .setting-control > label { margin: 0; }
.setting-control > .segmented { flex: none; }
.setting-control .inline-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; width: 100%; }
.setting-control .inline-actions > input { flex: 1; min-width: 160px; }
.list-rows { list-style: none; margin: 0; padding: 0; }
.list-rows > li { display: flex; align-items: flex-start; gap: 14px; padding: 14px 0; border-top: 1px solid var(--line); }
.list-rows > li:first-child { border-top: none; }
.list-rows .lr-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.list-rows .lr-title { font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.list-rows .lr-body { color: var(--muted); font-size: 13px; white-space: pre-wrap; overflow-wrap: anywhere; }
.list-rows .lr-actions { display: flex; gap: 6px; align-items: center; flex: none; }
.empty-line { color: var(--muted); font-size: 13px; padding: 18px 0 6px; text-align: center; }
.value-text { font-size: 14px; overflow-wrap: anywhere; }
.form-foot { display: flex; justify-content: flex-end; gap: 8px; padding-top: 14px; border-top: 1px solid var(--line); margin-top: 2px; }
.settings .profile-row { gap: 14px; }
.settings .profile-actions .hint { flex-basis: 100%; }

/*
 * Every plain "label + control" field that sits straight in a settings card
 * reads as a setting row too, without each tab spelling one out: the label's
 * words take the left column (first free cell), its hint the cell under them,
 * and the control the right column across both.
 */
.settings .panel > label:not(.setting):not(.check-line):not(.check-item),
.settings .panel > form > label:not(.setting):not(.check-line):not(.check-item) {
  display: grid; grid-template-columns: minmax(0, 290px) minmax(0, 1fr); column-gap: 28px; row-gap: 3px;
  align-items: start; margin: 0; padding: 16px 0; border-top: 1px solid var(--line);
  font-size: 14px; font-weight: 600; color: var(--text);
}
.settings .panel > label:not(.setting) > .hint,
.settings .panel > form > label:not(.setting) > .hint {
  grid-column: 1; grid-row: 2; font-weight: 400; line-height: 1.45;
}
.settings .panel > label:not(.setting) > :is(input, select, textarea, .color-field, .range-field, div),
.settings .panel > form > label:not(.setting) > :is(input, select, textarea, .color-field, .range-field, div) {
  grid-column: 2; grid-row: 1 / span 2; margin-top: 0; font-weight: 400;
}
.settings .panel-head + label, .settings .panel-head + form > label:first-child, .settings .intro + label { border-top: none; }
.setting-control > :is(input[type="text"], input[type="email"], input[type="password"], input[type="number"], input[type="url"], select) { max-width: 460px; }
@media (max-width: 900px) {
  .settings .panel > label:not(.setting), .settings .panel > form > label:not(.setting) { grid-template-columns: 1fr; }
  .settings .panel > label:not(.setting) > :is(input, select, textarea, .color-field, .range-field, div),
  .settings .panel > form > label:not(.setting) > :is(input, select, textarea, .color-field, .range-field, div) { grid-column: 1; grid-row: auto; }
  .settings .panel > label:not(.setting) > .hint { grid-row: auto; }
}

/* The first field under a card's title needs no rule above it. */
.settings .panel > .panel-head + label:not(.setting):not(.check-line):not(.check-item),
.settings .panel > .panel-head + form > label:first-child:not(.setting):not(.check-line):not(.check-item),
.settings .panel > .intro + label:not(.setting):not(.check-line):not(.check-item) { border-top: none; }
/* Fields laid out side by side (grids, dialogs) stay stacked: label over control. */
.settings .grid-2 > label, .settings .grid-3 > label, .dialog .grid-2 > label, .dialog .grid-3 > label {
  display: flex; flex-direction: column; gap: 6px; margin: 12px 0 0; padding: 0; border: none;
  font-size: 13px; font-weight: 600; color: var(--text);
}
.dialog .grid-2 > label .hint, .settings .grid-2 > label .hint { font-weight: 400; }
.dialog .grid-2 { gap: 0 16px; }
.dialog .setting { padding: 12px 0; }
.dialog .dialog-actions { margin-top: 12px; }
.form-dialog :is(input[type="text"], input[type="email"], input[type="password"], input[type="number"], input[type="url"], select, textarea) {
  width: 100%; box-sizing: border-box; padding: 8px 11px; border-radius: var(--radius); border: 1px solid var(--line-strong);
  background: var(--surface); color: var(--text); font-weight: 400;
}
.dialog.form-dialog { max-height: calc(100vh - 32px); overflow-y: auto; }
.settings .radio-list .check-item { font-weight: 400; }
.save-bar { padding-top: 4px; }
.settings .week { margin-top: 4px; }
.dialog label.check-item, .dialog .checks label { flex-direction: row; align-items: center; gap: 8px; font-weight: 400; }
.save-bar.sticky { bottom: 12px; background: var(--surface); border: none; border-radius: var(--radius-l); box-shadow: var(--lift, var(--shadow-pop)); padding: 12px 16px; margin-top: 4px; }

/* ------------------------------------------ full pages: tables as cards */

/* The list pages (visitors, customers, early access, the audit log) sit their
   table in a card like the settings do, with a quiet header row. */
.stats-page .table-wrap, .settings .table-wrap {
  background: var(--surface); border-radius: 20px; box-shadow: var(--lift, none); padding: 6px 8px; margin: 12px 0 16px;
}
.settings .panel .table-wrap { box-shadow: none; padding: 0; border-radius: 0; background: none; }
.data-table { font-size: 13.5px; }
.data-table th {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--faint); font-weight: 650;
  padding: 12px 12px 10px; border-bottom: 1px solid var(--line);
}
.data-table td { padding: 12px; }
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover td { background: color-mix(in srgb, var(--raised) 70%, transparent); }
.data-table td:first-child, .data-table th:first-child { padding-left: 16px; }
.data-table td:last-child, .data-table th:last-child { padding-right: 16px; }
.stats-head { align-items: center; }
.stats-head h1 { letter-spacing: -0.02em; }
.stats-page > .empty, .visitors-page .empty {
  background: var(--surface); border-radius: 20px; box-shadow: var(--lift, none); padding: 36px 20px; height: auto; margin-top: 12px;
}
.stats-filters input[type="search"], .stats-filters input[type="text"] { border-radius: 999px; }

/* Visitors: how many people are on each site right now; a click filters the table. */
.site-counts { display: flex; flex-wrap: wrap; gap: 10px; margin: 0 0 14px; }
.site-count { display: flex; align-items: center; gap: 12px; border: 1px solid var(--line); background: var(--surface); color: var(--text); border-radius: 14px; padding: 10px 14px; min-width: 150px; box-shadow: var(--lift, none); }
.site-count:hover { border-color: var(--line-strong); }
.site-count[aria-pressed="true"] { border-color: var(--sea); box-shadow: 0 0 0 3px var(--sea-soft); }
.site-count-name { flex: 1; text-align: left; font-weight: 600; font-size: 13.5px; }
.site-count-n { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--faint); }
.site-count.live .site-count-n { color: var(--ok); }
code.ip { font-size: 12px; color: var(--muted); background: none; padding: 0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
code.ip.block { display: block; margin-top: 2px; }
